/* global actualCostsReportsIndexUrl actualCostsReportsShowUrl actualCostsReportsDeleteUrl actualCostsReportsChannelName actualCostsReportsRefreshUrl actualCostsReportsPruningDays */

import React, { Fragment, useCallback, useEffect, useState } from 'react';
import ReactDOM from 'react-dom';

import App from 'App';
import { format } from 'date-fns';

import ArchivedReportView from './ArchivedReportView';

import { apiClient } from 'Helpers/network';

import IconButton from 'CommonComponents/IconButton';
import PaginationInfo from 'CommonComponents/PaginationInfo';
import PerPageSelect from 'CommonComponents/PerPageSelect';
import ScopeChips from 'CommonComponents/ScopeChips';
import SectionTile from 'CommonComponents/SectionTile';
import TablePagination from 'CommonComponents/TablePagination';
import TableRowSkeleton from 'CommonComponents/TableRowSkeleton';

import { ConfirmationModalProvider } from 'Context/ConfirmationModalContext';
import { ToastProvider } from 'Context/ToastContext';

import useApiRequest from 'Hooks/useApiRequest';
import { useConfirmationModal } from 'Hooks/useConfirmationModal';
import usePagination from 'Hooks/usePagination';
import { useToast } from 'Hooks/useToast';

import DownloadIcon from 'Assets/icons/download.svg';
import EyeIcon from 'Assets/icons/eye.svg';
import HourglassIcon from 'Assets/icons/hourglass.svg';
import InformationCircleIcon from 'Assets/icons/information-circle.svg';
import RefreshSyncIcon from 'Assets/icons/refresh-sync.svg';
import TrashIcon from 'Assets/icons/trash.svg';

const ArchivedActualCostsReports = ({
    actualCostsReportsIndexUrl,
    actualCostsReportsShowUrl,
    actualCostsReportsDeleteUrl,
    actualCostsReportsRefreshUrl,
    channelName,
    pruningDays,
}) => {
    const [viewingReportId, setViewingReportId] = useState(null);

    const pagination = usePagination({ perPage: 10 });

    const toast = useToast();

    const confirm = useConfirmationModal();

    const actualCostsReportsRequest = useApiRequest({
        url: actualCostsReportsIndexUrl,
    });

    const deleteReportRequest = useApiRequest({
        url: actualCostsReportsDeleteUrl,
    });

    useEffect(() => {
        getReports();
    }, [pagination.page, pagination.perPage]);

    useEffect(() => {
        if (actualCostsReportsRequest.data) {
            pagination.setFrom(actualCostsReportsRequest.data.meta.from);
            pagination.setTo(actualCostsReportsRequest.data.meta.to);
            pagination.setTotal(actualCostsReportsRequest.data.meta.total);
            pagination.setLastPage(actualCostsReportsRequest.data.meta.last_page);
        }
    }, [actualCostsReportsRequest.data]);

    const getReports = useCallback(() => {
        actualCostsReportsRequest.get({
            page: pagination.page,
            per_page: pagination.perPage,
        });
    }, [pagination.page, pagination.perPage]);

    // Subscribe to WebSocket for real-time status updates
    useEffect(() => {
        if (!channelName || !window.Echo) {
            return;
        }

        const channel = window.Echo.private(channelName);

        channel.listen('.report.generated', (event) => {
            if (event.report) {
                getReports();
            }
        });

        return () => {
            window.Echo.leave(channelName);
        };
    }, [channelName, getReports]);

    const deleteReport = (reportId) => {
        confirm({
            title: $t('Delete Report'),
            message: $t('Are you sure you want to delete this report?'),
            onConfirm: () => {
                deleteReportRequest.delete(null, { ACTUAL_COSTS_REPORT_ID: reportId }).then(() => {
                    getReports();
                });
            },
        });
    };

    const handleViewClick = (reportId) => {
        setViewingReportId((prev) => (prev === reportId ? null : reportId));
    };

    const refreshReport = (reportId) => {
        const url = actualCostsReportsRefreshUrl.replace('ACTUAL_COSTS_REPORT_ID', reportId);

        apiClient
            .post(url)
            .then(() => {
                getReports();
            })
            .catch(() => {
                toast.error($t('Failed to refresh report. Please try again.'));
            });
    };

    return (
        <Fragment>
            <div className="tw-space-y-4">
                <SectionTile header={$t('Reports')} defaultCollapsed={false} visibly>
                    <div className="tw-flex tw-items-center tw-justify-between tw-mb-6">
                        <div className="tw-flex tw-flex-col tw-gap-y-1">
                            <p className="tw-text-sm tw-text-gray-500">
                                {$t('Reports older than {{days}} days are deleted automatically.', { days: pruningDays })}
                            </p>
                            <div className="table__pagination-info">
                                <PaginationInfo to={pagination.to} from={pagination.from} total={pagination.total} />
                                <TablePagination
                                    lastPage={pagination.lastPage}
                                    pageChange={(data) => pagination.setPage(+data.selected + 1)}
                                    currentPage={pagination.page}
                                />
                            </div>
                        </div>
                        <div>
                            <PerPageSelect
                                value={pagination.perPage}
                                onChange={(e) => {
                                    pagination.setPerPage(parseInt(e.target.value));
                                }}
                            />
                        </div>
                    </div>
                    <div className="inbud-table_wrapper">
                        <table className="inbud-table">
                            <thead>
                                <tr>
                                    {/* Departments is fixed-width, User has a floor and dates stay on one line;
                                        the rest wrap so the table fits a standard desktop page. */}
                                    <th>{$t('Date')}</th>
                                    <th>{$t('Status')}</th>
                                    <th className="tw-min-w-24">{$t('User')}</th>
                                    <th>{$t('Name')}</th>
                                    <th>{$t('Budget')}</th>
                                    <th>{$t('Reporting Period')}</th>
                                    <th className="tw-w-80">{$t('Departments')}</th>
                                    <th>{$t('Generated')}</th>
                                    <th>{$t('Actions')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {actualCostsReportsRequest.loading && <TableRowSkeleton rows={10} columns={9} />}

                                {!actualCostsReportsRequest.loading &&
                                    actualCostsReportsRequest.data?.data &&
                                    actualCostsReportsRequest.data.data.length > 0 &&
                                    actualCostsReportsRequest.data.data.map((report) => (
                                        <tr key={report.id}>
                                            <td className="inbud-date-cell tw-whitespace-nowrap">
                                                {format(new Date(report.created_at), 'yyyy-MM-dd HH:mm:ss')}
                                            </td>
                                            <td>{report.status}</td>
                                            <td>{report.user_name}</td>
                                            <td>{report.name}</td>
                                            <td>{report.budget_name}</td>
                                            <td className="inbud-date-cell">{report.includes}</td>
                                            <td className="tw-w-80">
                                                <ScopeChips items={report.department_names ?? []} all={report.all_departments_included} />
                                            </td>
                                            <td className="inbud-date-cell tw-whitespace-nowrap">
                                                {report.generated_at ? format(new Date(report.generated_at), 'yyyy-MM-dd HH:mm:ss') : '-'}
                                            </td>
                                            <td>
                                                <div className="tw-flex tw-items-center">
                                                    {report.is_complete && (
                                                        <Fragment>
                                                            {report.download_url && (
                                                                <a href={report.download_url} className="text-decoration-none">
                                                                    <IconButton variant="success" size="sm" ghost title={$t('Download')}>
                                                                        <DownloadIcon className="tw-w-3.5 tw-h-3.5" />
                                                                    </IconButton>
                                                                </a>
                                                            )}
                                                            {report.source === 'cache' && (
                                                                <Fragment>
                                                                    <IconButton
                                                                        onClick={() => handleViewClick(report.id)}
                                                                        variant="primary"
                                                                        size="sm"
                                                                        ghost
                                                                        title={$t('View')}
                                                                    >
                                                                        <EyeIcon className="tw-w-3.5 tw-h-3.5" />
                                                                    </IconButton>
                                                                    <IconButton
                                                                        onClick={() => refreshReport(report.id)}
                                                                        variant="primary"
                                                                        size="sm"
                                                                        ghost
                                                                        title={$t('Refresh')}
                                                                    >
                                                                        <RefreshSyncIcon className="tw-w-3.5 tw-h-3.5" />
                                                                    </IconButton>
                                                                </Fragment>
                                                            )}
                                                        </Fragment>
                                                    )}
                                                    {report.is_failed && (
                                                        <Fragment>
                                                            <div className="tw-flex tw-justify-center tw-items-center tw-w-6 tw-h-6 tw-rounded-full">
                                                                <InformationCircleIcon
                                                                    className="tw-w-3.5 tw-h-3.5 tw-text-red-500"
                                                                    data-popper-toggle="tooltip"
                                                                    data-placement="left"
                                                                    title="Failed"
                                                                />
                                                            </div>
                                                            {report.source === 'cache' && (
                                                                <IconButton
                                                                    onClick={() => refreshReport(report.id)}
                                                                    variant="primary"
                                                                    size="sm"
                                                                    ghost
                                                                    title={$t('Refresh')}
                                                                >
                                                                    <RefreshSyncIcon className="tw-w-3.5 tw-h-3.5" />
                                                                </IconButton>
                                                            )}
                                                        </Fragment>
                                                    )}
                                                    {report.is_in_progress && (
                                                        <div className="tw-flex tw-justify-center tw-items-center tw-w-6 tw-h-6 tw-rounded-full">
                                                            <HourglassIcon
                                                                className="tw-w-3.5 tw-h-3.5 tw-text-yellow-500"
                                                                data-popper-toggle="tooltip"
                                                                data-placement="left"
                                                                title="In Progress"
                                                            />
                                                        </div>
                                                    )}
                                                    <IconButton variant="danger" size="sm" ghost title={$t('Delete')} onClick={(e) => deleteReport(report.id)}>
                                                        <TrashIcon className="tw-w-3 tw-h-3 tw-text-red" />
                                                    </IconButton>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                {!actualCostsReportsRequest.loading && actualCostsReportsRequest.data?.total === 0 && (
                                    <tr>
                                        <td colSpan="9">{$t('No Report found')}</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </SectionTile>

                {viewingReportId && (
                    <div className="table__card">
                        <ArchivedReportView reportId={viewingReportId} showUrl={actualCostsReportsShowUrl} />
                    </div>
                )}
            </div>
        </Fragment>
    );
};

if (document.getElementById('ArchivedActualCostsReports')) {
    var rootEl = document.getElementById('ArchivedActualCostsReports');
    ReactDOM.render(
        <App>
            <ToastProvider>
                <ConfirmationModalProvider>
                    <ArchivedActualCostsReports
                        actualCostsReportsIndexUrl={actualCostsReportsIndexUrl}
                        actualCostsReportsShowUrl={actualCostsReportsShowUrl}
                        actualCostsReportsDeleteUrl={actualCostsReportsDeleteUrl}
                        actualCostsReportsRefreshUrl={actualCostsReportsRefreshUrl}
                        channelName={actualCostsReportsChannelName}
                        pruningDays={actualCostsReportsPruningDays}
                    />
                </ConfirmationModalProvider>
            </ToastProvider>
        </App>,
        rootEl,
    );
}

export default ArchivedActualCostsReports;
